import { Pipe, PipeTransform } from '@angular/core';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneNumberService } from '../services/phone-number.service';

/**
 * Formats a stored phone number for display.
 *
 * Impure for the same reason as StudioCurrencyPipe: the studio's country
 * resolves asynchronously after config loads, and a pure pipe would keep
 * showing the number formatted against the pre-load default.
 */
@Pipe({
  name: 'phone',
  pure: false
})
export class PhonePipe implements PipeTransform {
  constructor(private phoneNumberService: PhoneNumberService) {}

  transform(value: string | number | null | undefined, country?: CountryCode): string {
    if (value == null) return '';

    const raw = ('' + value).trim();
    if (!raw) return '';

    const cleaned = raw.replace(/\D/g, '');
    const defaultCountry = country || this.phoneNumberService.getFallbackCountry();

    // North American numbers keep their long-standing '(212) 555-1234' styling.
    // Almost every stored number is a bare 10-digit US one, so this path runs
    // first to guarantee those render exactly as they always have.
    if (!raw.startsWith('+')) {
      const national = this.formatNorthAmerican(cleaned);
      if (national && (defaultCountry === 'US' || defaultCountry === 'CA')) {
        return national;
      }
    }

    // Everything else is resolved against the studio's country, so a number
    // stored without a '+' but carrying its country code (an Italian studio's
    // '393382730453') is recognised as +39 338 273 0453 rather than being
    // rendered as a meaningless run of digits.
    const parsed = parsePhoneNumberFromString(raw, defaultCountry);
    if (parsed?.isValid()) {
      return parsed.country === defaultCountry && !raw.startsWith('+')
        ? parsed.formatNational()
        : parsed.formatInternational();
    }

    return this.formatNorthAmerican(cleaned) ?? cleaned;
  }

  /** Legacy US/Canada formatting, or null when the digits don't fit the pattern. */
  private formatNorthAmerican(cleaned: string): string | null {
    if (cleaned.length === 10) {
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '+1 (' + match[1] + ') ' + match[2] + '-' + match[3];
      }
    }

    return null;
  }
}
